import AddIcon from "@mui/icons-material/AddRounded";
import { Button } from "@mui/material";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { ObjectClassDefinition, ObjectPermissions } from "../../../../api/types/objectTypes";
import useClientContext from "../../../../context/useClientContext";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import { getColumnDefinitionForField } from "../../../common/columns/columnDefinitions";
import { FilterContextProvider } from "../../../common/filters/FilterContextProvider";
import { getInitialFilterState, getVisibleColumnsAndFiltersIds } from "../../../common/filters/filterState";
import { getFilterDefinitionForEntityField } from "../../../entityFields/entityFieldFilterDefinitions";
import { isFieldFilterable } from "../../../entityFields/helpers";
import InvestorCreateDialog from "./InvestorCreateDialog";
import InvestorsGrid from "./InvestorsGrid";
import { searchDefinition, staticFilterDefinitions } from "./filterDefinitions";
import { getInvestorPath, staticColumnDefinitions } from "./investorsGridDefinitions";

interface Props {
  objectClassDefinition: ObjectClassDefinition;
  objectPermissions: ObjectPermissions;
}

interface DialogState {
  openDialog: "create_investor" | undefined;
}

const InvestorsPageContainer = ({ objectClassDefinition, objectPermissions }: Props) => {
  const { hasAnyPermission, clientCode, dictionaries } = useClientContext();
  const navigate = useNavigate();

  const [dialogState, setDialogState] = useState<DialogState>({ openDialog: undefined });

  const [filterState, setFilterState] = useState(() => {
    const fieldFilterDefinitions = objectClassDefinition.fields
      .filter(isFieldFilterable)
      .map((field) => getFilterDefinitionForEntityField(field, dictionaries));

    const fieldColumnDefinitions = objectClassDefinition.fields.map((field) => getColumnDefinitionForField(field));

    return getInitialFilterState(
      `${clientCode}_investors_filter_v2`,
      [...staticFilterDefinitions, ...fieldFilterDefinitions],
      [],
      [...staticColumnDefinitions, ...fieldColumnDefinitions],
      searchDefinition
    );
  });

  const getInvestors = useCallback(async () => {
    const fieldIds = getVisibleColumnsAndFiltersIds(filterState);
    return adminApi.searchInvestors({
      fieldIds,
      includeContacts: true,
      includeFunds: true,
      includeLookupObjects: true,
    });
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [filterState.visibleColumns, filterState.visibleFilters]);

  const [investorsResp, fetchInvestorsError, { isFetching: isFetchingInvestors }] = useFetch(getInvestors);

  const handleNavigateToInvestor = useCallback(
    (investorId: string) => navigate(getInvestorPath(investorId)),
    [navigate]
  );

  if (fetchInvestorsError) {
    logError(fetchInvestorsError, "[InvestorsPageContainer]");
    return <DataLoadingFailed title="Failed to load investors" />;
  }

  const investors = investorsResp?.items ?? [];

  const hasAccessToManage = hasAnyPermission(objectPermissions.objectDataWritePermissions ?? []);

  const isLoading = isFetchingInvestors || investorsResp === undefined;

  return (
    <>
      <GeneralPageHeader title="Investors">
        {hasAccessToManage && (
          <Button
            sx={{ whiteSpace: "nowrap" }}
            onClick={() => setDialogState({ openDialog: "create_investor" })}
            variant="contained"
            startIcon={<AddIcon />}
            disabled={isLoading}
          >
            New Investor
          </Button>
        )}
      </GeneralPageHeader>
      <FilterContextProvider
        filterState={filterState}
        setFilterState={setFilterState}
        allRowsForSelectOptions={investors}
      >
        <InvestorsGrid investors={investors} fields={objectClassDefinition.fields} isLoading={isLoading} />
      </FilterContextProvider>
      <InvestorCreateDialog
        open={dialogState.openDialog === "create_investor"}
        onClose={() => setDialogState({ openDialog: undefined })}
        existingInvestorNames={investors.map((investor) => investor.title)}
        onCreated={handleNavigateToInvestor}
      />
    </>
  );
};

export default InvestorsPageContainer;
